import { useMemo } from 'react';
import type { EChartsCoreOption } from 'echarts/core';
import { Grafico, LEGENDA } from '../../design/graficos/Grafico';
import { reais } from '../../premiacao';
import { useValoresOcultos } from '../../design/sigilo';
import type {
  ChaveDoTicket,
  DiaDaSerie,
  MarcaParaBater,
  MeuPainel,
  MetaDoTicket,
} from '../../vendas';

/**
 * "BATA A SUA MARCA" e "METAS DO MÊS" do Meu painel (dono, 25/09/2026).
 *
 * A pessoa contra ELA MESMA: ticket médio, % já feito do período anterior e quantos clientes por
 * dia (no ticket dela) faltam para passar. Nenhum total em R$ — o servidor nem manda — e nem a
 * contagem de clientes do período/dia (clientes × ticket = total vendido). Metas: +5%, +10%, +15% sobre a média dela no mês passado, em cada média.
 */

const MES = (anoMes: string) =>
  new Date(`${anoMes}-15T12:00:00Z`).toLocaleDateString('pt-BR', {
    month: 'long',
    timeZone: 'UTC',
  });
const DIA = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const ticketTxt = (t: string | null) => (t ? reais(Number(t)) : '—');

export function BataASuaMarca({ e }: { e: NonNullable<MeuPainel['evolucao']> }) {
  const ocultos = useValoresOcultos();
  // tudo aqui é ticket médio em R$ (quanto ele vendeu): no modo só números a seção inteira sai
  if (ocultos) return null;
  const mesAtual = MES(e.mes.atual.de.slice(0, 7));
  const mesPassado = MES(e.mes.anterior.de.slice(0, 7));
  return (
    <section className="ev" aria-label="Bata a sua marca">
      <h2 className="dp-secao">Bata a sua marca</h2>
      <div className="ev-grade">
        <CartaoDaMarca
          titulo={`Mês de ${mesAtual}`}
          contra={`${mesPassado}`}
          mesmoPonto={`${mesPassado} até o dia ${String(diaDeHojeBr())}`}
          m={e.mes}
        />
        <CartaoDaMarca
          titulo={`Semana ${DIA(e.semana.atual.de)} a ${DIA(e.semana.atual.ate)}`}
          contra="a semana passada"
          mesmoPonto="a semana passada até o mesmo dia"
          m={e.semana}
        />
      </div>
      <GraficosDoMes serie={e.mes.serie} atual={mesAtual} anterior={mesPassado} />
    </section>
  );
}

/** O dia de hoje em Brasília (o "mesmo ponto" do mês passado é este dia do mês). */
function diaDeHojeBr(): number {
  return Number(
    new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(8, 10),
  );
}

function CartaoDaMarca({
  titulo,
  contra,
  mesmoPonto,
  m,
}: {
  titulo: string;
  contra: string;
  mesmoPonto: string;
  m: MarcaParaBater;
}) {
  // Sem "% já feito" nem "quanto falta para passar" (dono, 27/09/2026): só os tickets médios.
  const atual = m.ateAgora.ticket;
  const antes = m.anteriorAteMesmoPonto.ticket;
  return (
    <div className="dp-cartao ev-cartao">
      <div className="ev-topo">
        <h3>{titulo}</h3>
        <span className="ev-contra">contra {contra}</span>
      </div>
      <div className="ev-duas">
        <div>
          <div className="ev-rotulo">Até agora</div>
          <div className="ev-num">{ticketTxt(m.ateAgora.ticket)}</div>
          <div className="ev-sub">ticket médio</div>
        </div>
        <div>
          <div className="ev-rotulo">{mesmoPonto}</div>
          <div className="ev-num">{ticketTxt(m.anteriorAteMesmoPonto.ticket)}</div>
          <div className="ev-sub">ticket médio</div>
        </div>
      </div>
      {atual === null || antes === null ? (
        <div className="ev-alvo ev-alvo-neutro">Sem venda sua em {contra} para comparar.</div>
      ) : Number(atual) >= Number(antes) ? (
        <div className="ev-alvo ev-alvo-ok">
          Seu ticket médio está acima do de {contra} até o mesmo ponto. Continue assim!
        </div>
      ) : (
        <div className="ev-alvo">
          Para bater {contra}, suba o ticket médio para mais de <b>{reais(Number(antes))}</b>.
        </div>
      )}
    </div>
  );
}

function GraficosDoMes({
  serie,
  atual,
  anterior,
}: {
  serie: readonly DiaDaSerie[];
  atual: string;
  anterior: string;
}) {
  const dias = serie.map((d) => String(d.dia));
  const num = (v: string | null) => (v === null ? null : Number(v));
  const ticket = useMemo<EChartsCoreOption>(
    () => ({
      grid: { left: 58, right: 12, top: 34, bottom: 28 },
      legend: { ...LEGENDA, top: 0, left: 0 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: unknown) => (typeof v === 'number' ? reais(v) : '—'),
      },
      xAxis: { type: 'category', data: dias, axisTick: { show: false } },
      yAxis: { type: 'value', axisLabel: { formatter: (v: number) => `R$ ${String(v)}` } },
      series: [
        {
          name: atual,
          type: 'line',
          smooth: true,
          connectNulls: false,
          data: serie.map((d) => num(d.ticketAtual)),
        },
        {
          name: anterior,
          type: 'line',
          smooth: true,
          lineStyle: { type: 'dashed' },
          data: serie.map((d) => num(d.ticketAnterior)),
        },
      ],
    }),
    [serie, atual, anterior],
  );
  return (
    <div className="ev-graficos">
      <div className="dp-cartao">
        <h3 className="ev-graf-titulo">Seu ticket médio por dia</h3>
        <Grafico
          opcao={ticket}
          altura={220}
          rotulo={`Ticket médio por dia, ${atual} contra ${anterior}`}
        />
      </div>
    </div>
  );
}

const TICKETS: readonly { k: ChaveDoTicket; titulo: string; reais: boolean }[] = [
  { k: 'geral', titulo: 'Ticket médio geral', reais: true },
  { k: 'itensPorCupom', titulo: 'Itens por cupom', reais: false },
  { k: 'produtosPorCupom', titulo: 'Produtos diferentes por cupom', reais: false },
  { k: 'porVenda', titulo: 'Ticket por venda no InovaPay', reais: true },
  { k: 'vitamina', titulo: 'Ticket da vitamina', reais: true },
  { k: 'preVencido', titulo: 'Ticket do pré-vencido', reais: true },
  { k: 'paradao', titulo: 'Ticket do paradão', reais: true },
  { k: 'entrega', titulo: 'Ticket de entrega', reais: true },
];

export function MetasDoMes({ metas }: { metas: Record<ChaveDoTicket, MetaDoTicket> }) {
  const ocultos = useValoresOcultos();
  // metas em R$ somem no modo só números; as de itens/produtos por cupom ficam
  const visiveis = TICKETS.filter((t) => !(ocultos && t.reais));
  return (
    <section aria-label="Metas do mês">
      <h2 className="dp-secao">Metas do mês · +5%, +10% e +15% sobre o seu mês passado</h2>
      <div className="mt-grade">
        {visiveis.map((t) => (
          <CartaoDaMeta key={t.k} titulo={t.titulo} emReais={t.reais} m={metas[t.k]} />
        ))}
      </div>
    </section>
  );
}

function CartaoDaMeta({
  titulo,
  emReais,
  m,
}: {
  titulo: string;
  emReais: boolean;
  m: MetaDoTicket;
}) {
  const f = (v: string | null) =>
    v === null
      ? ''
      : emReais
        ? reais(Number(v))
        : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  if (m.situacao === 'sem_base')
    return (
      <div className="dp-cartao mt-cartao mt-sem">
        <div className="mt-titulo">{titulo}</div>
        <div className="mt-texto">
          Sem venda sua nisso no mês passado: a meta começa no mês que vem.
        </div>
      </div>
    );
  const proxima = m.alvos[m.batidas];
  const v = m.variacaoPct;
  return (
    <div className={`dp-cartao mt-cartao${m.batidas === 3 ? ' mt-ouro' : ''}`}>
      <div className="mt-titulo">{titulo}</div>
      <div className="mt-linha">
        <span className="mt-atual">{m.atual ? f(m.atual) : '—'}</span>
        {v !== null && (
          <span className={`mt-var${v >= 0 ? ' mt-var-mais' : ' mt-var-menos'}`}>
            {v >= 0 ? '+' : ''}
            {v.toLocaleString('pt-BR')}%
          </span>
        )}
      </div>
      <div className="mt-texto">mês passado: {f(m.base)}</div>
      <ol className="mt-degraus">
        {m.alvos.map((a, i) => (
          <li key={a.pct} className={i < m.batidas ? 'mt-feito' : ''}>
            <span>+{a.pct}%</span>
            <b>{f(a.valor)}</b>
          </li>
        ))}
      </ol>
      <div className="mt-texto mt-chamada">
        {m.batidas === 3
          ? 'As 3 metas batidas! Você está 15% ou mais acima do mês passado.'
          : m.atual === null
            ? `Comece: a 1ª meta é ${proxima ? f(proxima.valor) : ''}.`
            : m.batidas === 0
              ? `Próxima meta: +5% = ${proxima ? f(proxima.valor) : ''}.`
              : `Você já bateu +${String(m.alvos[m.batidas - 1]?.pct ?? 0)}%. Tente mais 5%: ${proxima ? f(proxima.valor) : ''}.`}
      </div>
    </div>
  );
}
